import { formatPercentage } from "utils/currency.utils";
import { getLogoUrl, getFundInitial } from "utils/getLogoUrl";

/**
 * Mutual Funds API response parsers
 * Transform API responses to app-friendly format
 */

/**
 * Parse mutual funds error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */

const getField = (obj, keys, defaultValue = null) => {
  if (!obj) return defaultValue;
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return defaultValue;
};

export const parseMutualFundsErrorResponse = (error) => {
  if (!error) {
    return {
      success: false,
      message: "Unknown error occurred",
      code: "UNKNOWN_ERROR",
      details: null,
    };
  }

  const { data, status } = error;

  if (data?.code) {
    return {
      success: false,
      code: data?.code,
      message: data?.message || "An error occurred",
      details: data?.details || null,
    };
  }

  return {
    success: false,
    message: data?.message || data?.error || "An error occurred",
    code: data?.code || `HTTP_${status}`,
    details: data?.details || null,
    status: status || 500,
  };
};

/**
 * Parse mutual fund history response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed history data
 */
export const parseMutualFundHistoryResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || "Failed to fetch mutual fund history",
      code: response?.code || "UNKNOWN_ERROR",
      history: null,
    };
  }

  // Map the data array from result.data
  const historyData = Array.isArray(response?.result?.data)
    ? response?.result?.data?.map((item) => ({
      date: item?.date || null,
      nav: item?.nav || 0,
      returns: item?.returns || 0,
    }))
    : [];

  return {
    success: true,
    message: response?.message || "Mutual fund history fetched successfully",
    code: response?.code,
    history: historyData,
    tenure: response?.result?.tenure || null,
    summary: response?.result?.summary || null,
    requestId: response?.requestId,
  };
};

/**
 * Parse mutual fund by ID response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed mutual fund data
 */
//////////
export const parseMutualFundByIdResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || "Failed to fetch mutual fund details",
      code: response?.code || "UNKNOWN_ERROR",
      fund: null,
    };
  }

  const fund = response.result;

  // Extract nested object values
  // Handle fundManagers array (API returns array of managers with name, manager_exp, manager_startdate)
  const fundManager = (() => {
    // Check if fundManagers is an array
    if (Array.isArray(fund?.fundManagers) && fund.fundManagers.length > 0) {
      // Concatenate all manager names with commas
      return fund.fundManagers.map(m => m?.name || '').filter(Boolean).join(', ');
    }
    // Fallback to old format
    if (typeof fund?.fundManager === 'object' && fund.fundManager?.managerName) {
      return fund.fundManager.managerName;
    }
    if (typeof fund?.fundManager === 'string') {
      return fund.fundManager;
    }
    return null;
  })();

  // Extract manager experience (from first manager if multiple)
  const managerExperience = (() => {
    if (Array.isArray(fund?.fundManagers) && fund.fundManagers.length > 0) {
      const exp = fund.fundManagers[0]?.manager_exp;
      return exp ? parseFloat(exp) : null;
    }
    if (fund?.fundManager?.yearsOfExperience) {
      return parseFloat(fund.fundManager.yearsOfExperience);
    }
    return null;
  })();

  // Extract manager start date (from first manager if multiple)
  const managerStartDate = (() => {
    if (Array.isArray(fund?.fundManagers) && fund.fundManagers.length > 0) {
      return fund.fundManagers[0]?.manager_startdate || null;
    }
    if (fund?.fundManager?.startDate) {
      return fund.fundManager.startDate;
    }
    return fund?.managerStartDate || null;
  })();

  const fundCategory = typeof fund?.fundCategory === 'object'
    ? fund.fundCategory?.name
    : fund?.fundCategory;

  const fundAMC = typeof fund?.fundAMC === 'object'
    ? fund.fundAMC?.name
    : fund?.fundAMC;

  // Parse the fund data with all fields
  const parsedFund = {
    // Basic Info
    fundId: fund?.fundId || null,
    fundName: (fund?.fundName && fund.fundName.trim() !== '') ? fund.fundName : (fund?.morningStarName || null),
    morningStarName: fund?.morningStarName || null,

    // Nested objects - extract string values
    fundManager: fundManager || null,
    fundManagers: fund?.fundManagers || null, // Keep full array for detailed display
    managerExperience: managerExperience || null,
    fundCategory: fundCategory || null,
    categoryName: fund?.categoryName || fundCategory || null,
    fundHouse: fundAMC || null,
    fundAMC: fund?.fundAMC || null, // Keep full object if needed

    // Ratings
    morningStarRating: fund?.morningStarRating || null,
    inxitsRating: fund?.inxitsRating || null,

    // Financial Data
    fundSize: fund?.fundSize || null,
    aum: fund?.aum || fund?.fundSize || null,
    nav: fund?.nav || null,
    currentNav: fund?.nav || null,
    navChange: fund?.navChange || null,
    navChangePercentage: fund?.navChangePercentage || null,
    navDate: fund?.navDate || null, // If provided by API
    managerStartDate: managerStartDate || null,

    // Returns
    returns: fund?.returns || null,
    oneYearReturn: fund?.returns?.["1year"] || fund?.returns?.oneYear || null,
    threeYearReturn: fund?.returns?.["3years"] || fund?.returns?.threeYears || null,
    tenYearReturn: fund?.returns?.["10years"] || fund?.returns?.tenYears || null,

    // Dates
    inceptionDate: fund?.inceptionDate || null,

    // Investment Details
    minInvestment: fund?.minInvestment || null,
    expenseRatio: fund?.expenseRatio || null,
    expenseRatioDate: fund?.expenseRatioDate || null,
    exitLoad: fund?.exitLoad || null,
    lockInPeriod: fund?.lockInPeriod || null,

    // Fund Structure
    businessStructure: fund?.businessStructure || fund?.structure || null,
    fundType: fund?.fundType || null,
    category: fund?.category || null,
    marketCap: fund?.marketCap || null,

    // Risk & Benchmark
    risk: fund?.risk || null,
    riskLevel: fund?.risk || fund?.riskLevel || null,
    benchmark: fund?.benchmark || null,

    // Additional Info
    age: fund?.age || null,
    aboutFund: fund?.aboutFund || null,

    // Taxation
    shortTermCapitalGains: fund?.shortTermCapitalGains || null,
    longTermCapitalGains: fund?.longTermCapitalGains || null,

    // BSE Parameters
    bseLumpsumParams: fund?.bseLumpsumParams || null,
    schemeBseCode: fund?.bseLumpsumParams?.schemeBseCode || null,
    bseUniquecode: fund?.bseLumpsumParams?.bseUniquecode || null,

    // Market Allocation
    scheme_market_allocation: fund?.scheme_market_allocation || null,

    // 52 Weeks High/Low
    five2WeeksHigh: fund?.five2WeeksHigh || fund?.fiftyTwoWeeksHigh || fund?.week52High || null,
    five2WeeksLow: fund?.five2WeeksLow || fund?.fiftyTwoWeeksLow || fund?.week52Low || null,
    week52High: fund?.week52High || fund?.five2WeeksHigh || fund?.fiftyTwoWeeksHigh || null,
    week52Low: fund?.week52Low || fund?.five2WeeksLow || fund?.fiftyTwoWeeksLow || null,
    week52HighDate: fund?.week52HighDate || null,
    week52LowDate: fund?.week52LowDate || null,
    fifty2WeekHigh: fund?.week52High || fund?.five2WeeksHigh || fund?.fiftyTwoWeeksHigh || null,
    // 52 Week to Current NAV Percentage
    fifty2WeekToCurrentNavPercent: fund?.["52_week_to_current_nav_percent"] || fund?.fifty2WeekToCurrentNavPercent || null,

    // Logo
    logo: getLogoUrl(fund),
    fundLogo: getLogoUrl(fund),

    // SIP Investment
    minSIPInvestment: fund?.minSIPInvestment || null,

    // Keep original result for any fields we might have missed (but override with parsed values)
    ...fund,

    // Override with parsed string values to prevent object rendering issues
    fundManager: fundManager || fund?.fundManager || null,
    fundCategory: fundCategory || fund?.fundCategory || null,
    fundHouse: fundAMC || fund?.fundHouse || null,
    // Ensure risk fields are properly set
    risk: fund?.risk || null,
    riskLevel: fund?.risk || fund?.riskLevel || null,
  };

  return {
    success: true,
    message: response?.message || "Mutual fund details fetched successfully",
    code: response?.code,
    fund: parsedFund,
    requestId: response?.requestId,
  };
};

/**
 * Parse mutual fund holdings response
 * @param {Object} response - Raw API response
 * @returns {Array} Parsed holdings data
 */
export const parseMutualFundHoldingsResponse = (response) => {
  if (!response || !response.result) {
    return [];
  }

  let holdingsArray = [];

  // Check if data is nested under result.data
  if (Array.isArray(response.result.data)) {
    holdingsArray = response.result.data;
  } else if (Array.isArray(response.result)) {
    // If result is already an array, use it
    holdingsArray = response.result;
  } else {
    return [];
  }

  // Transform each holding item to standardized keys
  return holdingsArray?.map((item) => ({
    name: item?.name || item?.holdingName || "",
    sector: item?.sector || item?.sectorName || "",
    percentage: parseFloat(item?.percentage || 0),
    sectorName: item?.sectorName || item?.sector || "",
  }));
};

// Additional parser functions will be added here as endpoints are implemented
//  * Parse mutual fund collections response
//  * @param {Object} response - Raw API response
//  * @returns {Object} Parsed collections data
//  */
export const parseMutualFundCollectionsResponse = (response) => {

  if (!response) {
    return {
      success: false,
      message: "Invalid response format",
      code: "INVALID_RESPONSE",
      collections: [],
      requestId: null,
    };
  }

  const code = response?.code ?? null;
  const statusCode = response?.statusCode ?? null;
  const message = response?.message ?? "";
  const result = response?.result ?? null;
  const requestId = response?.requestId ?? null;

  const isSuccess =
    statusCode === 200 ||
    statusCode === "200" ||
    code === "200" ||
    code === 200 ||
    code === "SUCCESS";

  let collectionsArray = [];

  if (Array.isArray(result?.data)) {
    collectionsArray = result.data;
  } else if (Array.isArray(result)) {
    collectionsArray = result;
  }

  const collections = collectionsArray.map((collection, index) => {

    // ✅ Absolute Returns Safe Mapping
    const returnsValue = collection?.absoluteReturns;
    // const absoluteReturns = collection?.absoluteReturns;
    // const absoluteReturns = collection?.absoluteReturns;

    // const returnsValue = absoluteReturns;
    // ✅ Top 3 AMC Logos Safe Mapping
    const badges = (collection?.top3Funds ?? [])
      .filter(fund => fund?.amcLogo)
      .slice(0, 3)
      .map((fund) => ({
        type: "image",
        src: getLogoUrl(fund),
        label: fund?.amcName ?? ""
      }));

    return {
      ...collection,

      id:
        collection?.id ??
        collection?.collectionId ??
        index + 1,

      icon: collection?.icon ?? "",
      title: collection?.title ?? "",
      subTitle: collection?.subTitle ?? "",
      desc: collection?.description ?? "",

      // ReturnsStrip
      returns: returnsValue,
      duration: "3 Y Abs. returns",
      badges: badges
    };

  });

  return {
    success: isSuccess,
    code: code ?? "SUCCESS",
    statusCode: statusCode ?? null,
    message: message || "Collections retrieved successfully",
    collections: collections ?? [],
    requestId: requestId ?? null,
  };

};

/**
 * Parse popular funds response
 * POST /api/mutual-funds/popular-funds
 *
 * @purpose Parse popular mutual funds response
 * @useCase Display popular funds in dashboard/homepage with category filtering
 *
 * @response {Object}
 * @response {string} code - Response code (e.g., "MFDS0001")
 * @response {number} statusCode - HTTP status code (200)
 * @response {string} message - Success message
 * @response {Object} result - Response data
 * @response {Object} result.data - Object with category keys (equity, debt, hybrid)
 * @response {Array} result.data.equity - Array of equity funds
 * @response {Array} result.data.debt - Array of debt funds
 * @response {Array} result.data.hybrid - Array of hybrid funds
 * @response {string} requestId - Request ID
 */
export const parsePopularFundsResponse = (response) => {
  try {
    if (!response || !response.result) {
      return {
        success: false,
        message: response?.message || "Invalid response format - no data received",
        code: response?.code || "UNKNOWN_ERROR",
        funds: {},
        requestId: null,
      };
    }

    // Extract response data with optional chaining
    const { result, code, message, requestId } = response;
    const responseData = result?.data;

    if (!responseData || typeof responseData !== 'object') {
      return {
        success: false,
        message: message || "Invalid response format - missing data",
        code: code || "INVALID_RESPONSE",
        funds: {},
        requestId: requestId || null,
      };
    }

    // Categories from the API: equity, debt, hybrid
    const categories = ["equity", "debt", "hybrid"];
    const parsedFunds = {};

    categories.forEach(cat => {
      const categoryData = Array.isArray(responseData?.[cat])
        ? responseData[cat]
        : [];

      parsedFunds[cat] = categoryData
        ?.map(fund => parsePopularFundItem(fund))
        ?.filter(item => item !== null) || [];
    });

    return {
      success: true,
      code: code || "SUCCESS",
      message: message || "Popular funds retrieved successfully",
      funds: parsedFunds,
      requestId: requestId || null,
    };
  } catch (error) {
    return {
      success: false,
      message: "Error parsing response",
      code: "PARSE_ERROR",
      details: error?.message || "Unknown error",
      funds: {},
      requestId: response?.requestId || null,
    };
  }
};
/**
 * Parse individual popular fund item
 * @param {Object} fund - Fund object from API
 * @returns {Object|null} Parsed fund item or null if invalid
 */
const parsePopularFundItem = (fund) => {
  try {
    if (!fund || typeof fund !== 'object') {
      return null;
    }

    // Parse category string to extract category and risk (e.g., "ELSS (Tax Savings) • Very High Risk")
    const { category, risk } = parseCategoryString(fund?.category);

    // Format inception date (already in "DD MMM YYYY" format)
    const inceptionDate = fund?.inceptionDate || "";

    // Safe number parsing helper
    const safeParseFloat = (value, defaultValue = 0) => {
      if (value === null || value === undefined) return defaultValue;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    };

    // Convert fund size from rupees to crores with safe parsing
    const fundSizeValue = fund?.fundSize;
    const fundSizeInCrores = fundSizeValue && !isNaN(parseFloat(fundSizeValue))
      ? parseFloat(fundSizeValue) / 10000000
      : 0;
    return {
      id: fund?.fundId || fund?.isin || null,
      name: fund?.fundName || "",
      morningStarName: fund?.morningStarName || "",
      category: category || "Equity",
      risk: risk || "Moderate",
      nav: safeParseFloat(fund?.nav, 0),
      navChange: safeParseFloat(fund?.navChangePercentage, 0),
      morningStar: safeParseFloat(fund?.morningStarRating, 0),
      inxits: fund?.inxitRating !== null && fund?.inxitRating !== undefined
        ? safeParseFloat(fund?.inxitRating, 0)
        : 0,
      size: fundSizeInCrores,
      inception: inceptionDate,
      // Map cagr3Yr to expRatio (component uses expRatio to display CAGR)
      expRatio: safeParseFloat(fund?.cagr3Yr, 0),
      minInvestment: safeParseFloat(fund?.minInvestment, 0),
      logo: getLogoUrl(fund),
      weekLow52: fund?.weekLow52,
      weekHigh52: fund?.weekHigh52,
      asOnDate: fund?.as_on_date,
      inxitsScore: fund?.inxitsScore

    };
  } catch (error) {
    return null;
  }
};
export const parseMutualFundsResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: "Invalid response format",
      data: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      },
    };
  }

  const { result } = response;
  // Transform API data to match table structure
  const funds = (result.data || []).map((fund) => {
    // Extract nested object values
    const fundManager = typeof fund?.fundManager === 'object'
      ? fund.fundManager?.managerName
      : fund?.fundManager;

    const fundCategory = typeof fund?.fundCategory === 'object'
      ? fund.fundCategory?.name
      : fund?.fundCategory;

    const fundAMC = typeof fund?.fundAMC === 'object'
      ? fund.fundAMC?.name
      : fund?.fundAMC;

    // Map morningStarRating from API to inxitsRating (they are the same in this API)
    // Handle null case - morningStarRating can be null
    const inxitsRating = fund?.inxitsRating

    // Extract returns with fallbacks for both old and new API formats
    const returns = fund?.returns || {};

    return {
      id: fund?.fundId,
      fundId: fund?.fundId,
      fundName: fund?.fundName,
      morningStarName: fund?.morningStarName,
      fundManager: fundManager || null,
      sector: fundCategory || "N/A",
      rating: inxitsRating,
      fundSize: fund?.fundSize,
      oneYearReturn: formatReturn(returns?.["1Year"] ?? returns?.["1year"] ?? returns?.oneYear ?? null),
      twoYearReturn: formatReturn(returns?.["2Years"] ?? returns?.["2year"] ?? returns?.twoYears ?? null),
      threeYearReturn: formatReturn(returns?.["3Years"] ?? returns?.["3years"] ?? returns?.threeYears ?? null),
      fiveYearReturn: formatReturn(returns?.["5Years"] ?? returns?.["5year"] ?? returns?.fiveYears ?? null),
      tenYearReturn: formatReturn(returns?.["10Years"] ?? returns?.["10years"] ?? returns?.tenYears ?? null),
      sinceInceptionReturn: formatReturn(returns?.sinceInceptions ?? returns?.sinceInception ?? null),
      alpha3Yr: fund?.alpha3Yr,
      inceptionDate: formatDate(fund?.inceptionDate),
      minInvestment: formatCurrency(fund?.minInvestment),
      minSIPInvestment: formatCurrency(fund?.minSIPInvestment),
      fundCategory: fundCategory || null,
      morningStarRating: fund?.morningStarRating ?? 0,
      inxitsRating: inxitsRating,
      returns: returns,
      colorCodes: fund?.colorCodes || null, // ✅ ADD THIS LINE
      fundHouse: fundAMC || fund?.fundHouse || fund?.amc || fund?.amcName || null,
      logo: getLogoUrl(fund),
      schemeBseCode: fund?.bseLumpsumParams?.schemeBseCode || null,
      bseLumpsumParams: fund?.bseLumpsumParams || null,
    };
  });

  return {
    success: response?.statusCode === 200,
    message: response?.message || "Funds retrieved successfully",
    code: response?.code,
    data: funds,
    pagination: result.pagination || {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    },
    requestId: response?.requestId,
  };
};

/**
 * Parse risk profile funds response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed risk profile funds response data
 * 
 * Expected response structure:
 * {
 *   "code": "MFDS0003",
 *   "message": "Risk profile funds retrieved successfully",
 *   "result": {
 *     "data": [
 *       {
 *         "fundId": "MF001",
 *         "fundName": "HDFC Equity Fund",
 *         "fundCategory": "large",
 *         "nav": 245.67,
 *         "morningStarRating": 4,
 *         "inxitsRating": 4,
 *         "fundSize": 12500,
 *         "inceptionDate": "2010-05-15T00:00:00.000Z",
 *         "cagr": 14.5,
 *         "minInvestmentAmount": 5000
 *       }
 *     ],
 *     "pagination": {
 *       "total": 12,
 *       "page": 1,
 *       "limit": 10,
 *       "totalPages": 2
 *     }
 *   },
 *   "requestId": "req-1736940600000"
 * }
 */
export const parseRiskProfileResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || "Failed to fetch risk profile funds",
      code: response?.code || "UNKNOWN_ERROR",
      funds: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      },
      requestId: null,
    };
  }

  const fundsData = Array.isArray(response.result?.data)
    ? response.result.data.map((fund) => ({
      fundId: fund.fundId || null,
      fundName: fund.fundName || null,
      fundCategory: fund.fundCategory || null,
      nav: parseFloat(fund.nav) || 0,
      morningStarRating: parseFloat(fund.morningStarRating) || 0,
      inxitRating: parseFloat(fund.inxitRating) || 0,
      fundSize: parseFloat(fund.fundSize) || 0,
      inceptionDate: fund.inceptionDate || null,
      cagr: parseFloat(fund.cagr) || 0,
      minInvestmentAmount: parseFloat(fund.minInvestmentAmount) || 0,
      weekHigh52: parseFloat(fund.weekHigh52) || 0,
      weekLow52: parseFloat(fund.weekLow52) || 0,
    }))
    : [];

  return {
    success: true,
    message: response?.message || "Risk profile funds retrieved successfully",
    code: response?.code,
    funds: fundsData,
    pagination: response.result?.pagination || {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    },
    requestId: response?.requestId || null,
  };
};

/**
 * Format fund size to readable format
 * @param {number} size - Fund size in crores
 * @returns {string} Formatted fund size
 */
const formatFundSize = (size) => {
  if (!size) return "N/A";
  return `₹${(size / 100).toFixed(2)} Cr`;
};

/**
 * Format return percentage
 * @param {number} returnValue - Return value
 * @returns {string} Formatted return percentage
 */
const formatReturn = (returnValue) => {
  if (returnValue === null || returnValue === undefined) return null;
  const numValue = typeof returnValue === 'string' ? parseFloat(returnValue) : returnValue;
  if (isNaN(numValue)) return null;
  return numValue;
};

/**
 * Format date to readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

/**
 * Format currency to readable format
 * @param {number} amount - Amount in rupees
 * @returns {string} Formatted currency
 */
const formatCurrency = (amount) => {
  if (!amount) return "N/A";
  return `₹${amount.toLocaleString("en-IN")}`;
};

/**
 * Parse category string to extract category name and risk level
 * @param {string} categoryString - Category string like "Focused Fund • Very High Risk"
 * @returns {Object} Object with category and risk
 */
const parseCategoryString = (categoryString) => {
  if (!categoryString || typeof categoryString !== 'string') {
    return { category: "Equity", risk: "Moderate" };
  }

  const parts = categoryString.split('•').map(part => part.trim());
  if (parts.length >= 2) {
    return {
      category: parts[0] || "Equity",
      risk: parts[1] || "Moderate"
    };
  }

  return {
    category: parts[0] || "Equity",
    risk: "Moderate"
  };
};

/**
 * Parse recommendation funds response
 * POST /api/mutual-funds/recommendation-funds
 *
 * @purpose Get recommended mutual funds for the user
 * @useCase Display recommended funds in the explore page after risk assessment
 *
 * @response {Object}
 * @response {string} code - Response code (e.g., "MFDS0008")
 * @response {number} statusCode - HTTP status code (200)
 * @response {string} message - Success message
 * @response {Object} result - Response data
 * @response {Array} result.data - Array of recommended fund objects
 * @response {string} requestId - Request ID
 */
export const parseRecommendationFundsResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || "Failed to fetch recommendation funds",
      code: response?.code || "UNKNOWN_ERROR",
      funds: [],
      requestId: null,
    };
  }

  const fundsData = Array.isArray(response?.result?.data)
    ? response?.result?.data
      ?.map((fund) => {
        if (!fund || typeof fund !== 'object') {
          return null;
        }

        try {
          // Parse category string to extract category and risk
          const { category, risk } = parseCategoryString(fund?.category);

          // Fund size is assumed to be in Crores based on example values (e.g. 12500)
          const fundSizeValue = fund?.fundSize;
          const fundSizeInCrores = fundSizeValue && !isNaN(parseFloat(fundSizeValue))
            ? parseFloat(fundSizeValue)
            : 0;

          // Format inception date (already in "DD MMM YYYY" format, validate and use as-is)
          const formattedInception = fund?.inceptionDate || "";

          // Safe number parsing helper
          const safeParseFloat = (value, defaultValue = 0) => {
            if (value === null || value === undefined) return defaultValue;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? defaultValue : parsed;
          };

          return {
            fundId: fund?.fundId || null,
            isin: fund?.isin || null,
            fundName: fund?.fundName || null,
            morningStarName: fund?.morningStarName || null,
            category: category || "Equity",
            risk: risk || "Moderate",
            nav: safeParseFloat(fund?.nav, 0),
            navChangePercentage: safeParseFloat(fund?.navChangePercentage, 0),
            morningStarRating: safeParseFloat(fund?.morningStarRating, 0),
            inxitRating: fund?.inxitRating !== null && fund?.inxitRating !== undefined
              ? safeParseFloat(fund?.inxitRating, null)
              : null,
            fundSize: fundSizeInCrores,
            cagr3Yr: safeParseFloat(fund?.cagr3Yr, 0),
            inceptionDate: formattedInception,
            minInvestment: safeParseFloat(fund?.minInvestment, 0),
            weekLow52: fund?.weekLow52,
            weekHigh52: fund?.weekHigh52,
            asOnDate: fund?.as_on_date,
            inxitsScore: fund?.inxitsScore,
            logo: getLogoUrl(fund),

          };
        } catch (error) {
          return null;
        }
      })
      ?.filter(fund => fund !== null) || []
    : [];

  return {
    success: true,
    message: response?.message || "Recommendation funds retrieved successfully",
    code: response?.code || null,
    funds: Array.isArray(fundsData) ? fundsData : [],
    requestId: response?.requestId || null,
  };
};

/**
 * Parse manager list response
 * POST /api/mutual-funds/manager-list
 *
 * @purpose Parse manager list API response
 * @useCase Display fund managers in filter dropdown
 *
 * @param {Object} response - Raw API response
 * @response {string} code - Response code (e.g., "MFDS0011")
 * @response {string} message - Success message
 * @response {Array} result - Array of manager objects
 * @response {number} result[].id - Manager ID
 * @response {string} result[].name - Manager name
 * @returns {Object} Parsed manager list data
 */
export const parseManagerListResponse = (response) => {
  try {
    // Validate response structure
    const isSuccess = response?.code === "MFDS0011";
    const managers = Array.isArray(response?.result)
      ? response.result
        .filter(manager => manager?.id && manager?.name) // Filter out invalid entries
        .map(manager => ({
          id: manager?.id,
          name: manager?.name?.trim() || "", // Trim whitespace from names
        }))
        // Deduplicate by name to handle API duplicates
        .filter((item, index, self) =>
          index === self.findIndex((t) => t.name === item.name)
        )
      : [];

    return {
      success: isSuccess,
      message: response?.message || "Manager list retrieved successfully",
      code: response?.code || null,
      managers: managers,
    };
  } catch (error) {
    console.error("Error parsing manager list response:", error);
    return {
      success: false,
      message: "Error parsing manager list",
      code: "PARSE_ERROR",
      managers: [],
    };
  }
};

/**
 * Parse AMC list response
 * POST /api/mutual-funds/amc-list
 *
 * @purpose Parse AMC (fund house) list API response
 * @useCase Display fund houses in filter dropdown
 *
 * @param {Object} response - Raw API response
 * @response {string} code - Response code (e.g., "MFDS0010")
 * @response {string} message - Success message
 * @response {Array} result - Array of AMC objects
 * @response {number} result[].id - AMC ID
 * @response {string} result[].name - AMC name
 * @returns {Object} Parsed AMC list data
 */
export const parseAmcListResponse = (response) => {
  try {
    // Validate response structure
    const isSuccess = response?.code === "MFDS0010";
    const amcs = Array.isArray(response?.result)
      ? response.result
        .filter(amc => amc?.id && amc?.name) // Filter out invalid entries
        .map(amc => ({
          id: amc?.id,
          name: amc?.name?.trim() || "", // Trim whitespace from names
        }))
        // Deduplicate by name to handle API duplicates
        .filter((item, index, self) =>
          index === self.findIndex((t) => t.name === item.name)
        )
      : [];

    return {
      success: isSuccess,
      message: response?.message || "AMC list retrieved successfully",
      code: response?.code || null,
      amcs: amcs,
    };
  } catch (error) {
    console.error("Error parsing AMC list response:", error);
    return {
      success: false,
      message: "Error parsing AMC list",
      code: "PARSE_ERROR",
      amcs: [],
    };
  }
};

// Additional parser functions will be added here as endpoints are implemented

//  * Parse mutual fund history response
//  * @param {Object} response - Raw API response
//  * @returns {Object} Parsed fund history data
//  */
//   return response.result;
// };

/**
 * Parse category list response
 * POST /api/mutual-funds/category-list
 *
 * @purpose Parse category list API response
 * @useCase Display category filters (Equity, Debt, etc.)
 *
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed category list data
 */
export const parseCategoryListResponse = (response) => {
  try {
    // Validate response structure
    const isSuccess = response?.code === "MFDS0012" || response?.success === true;

    // Initialize default structure
    const categories = {
      equity: [],
      debt: [],
      hybrid: [],
      others: []
    };

    if (Array.isArray(response?.result)) {
      response.result.forEach(catItem => {
        const categoryName = catItem.category?.toLowerCase()?.trim();
        // Map API category to our internal keys
        // "Equity" -> "equity", "Debt" -> "debt", etc.
        let targetKey = "others";

        if (categoryName && ['equity', 'debt', 'hybrid'].includes(categoryName)) {
          targetKey = categoryName;
        }

        if (Array.isArray(catItem.subcategories)) {
          const options = catItem.subcategories.map(sub => ({
            label: sub.name,
            value: sub.name, // Use name as value to match label for filtering
            id: sub.id
          }));

          if (targetKey === 'others') {
            categories.others = [...categories.others, ...options];
          } else {
            categories[targetKey] = options;
          }
        }
      });
    }

    return {
      success: isSuccess,
      message: response?.message || "Category list retrieved successfully",
      code: response?.code || null,
      categories: categories,
    };
  } catch (error) {
    console.error("Error parsing category list response:", error);
    return {
      success: false,
      message: "Error parsing category list",
      code: "PARSE_ERROR",
      categories: {
        equity: [],
        debt: [],
        hybrid: [],
        others: []
      },
    };
  }
};

/**
 * Parse SWP parameters response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed SWP parameters
 */
export const parseSwpParamsResponse = (response) => {
  if (!response || !response.result) {
    return {
      success: false,
      message: response?.message || "Failed to fetch SWP parameters",
      code: response?.code || "UNKNOWN_ERROR",
      result: null,
    };
  }

  return {
    success: true,
    message: response?.message || "SWP parameters retrieved successfully",
    code: response?.code,
    result: response.result,
    requestId: response?.requestId,
  };
};
